import {
	addToTargetingExperiment,
	BaseServiceSetup,
	context,
	defineExperiment,
	Experiment,
	getExperiment,
	isExperimentEnabled,
	utils,
} from '@wikia/ad-engine';

type RunningExperimentType = Experiment | null | undefined;

const logGroup = 'Peer39Experiment';

/**
 * This experiment is used to test the Peer39 integration.
 */
export class Peer39Experiment extends BaseServiceSetup {
	private pathfinderExperimentVariantNames = {
		control: 'aden-14642-control',
		active: 'aden-14642-variant-1',
	};

	private pathfinderActiveExperimentVariant: RunningExperimentType = getExperiment([
		defineExperiment({
			name: this.pathfinderExperimentVariantNames.active,
			buckets: ['53', '54', '55'],
		}),
		defineExperiment({
			name: this.pathfinderExperimentVariantNames.control,
			buckets: ['50', '51', '52'],
		}),
	]);

	// List of safety IDs
	private readonly SAFETY_IDS = [
		'v9i3On',
		'I4GWl6',
		'Rm3SiT',
		'bsr004',
		'bsr003',
		'pg0WhF',
		'avbNf2',
		'Z7rJBM',
		'XtODT3',
		'I4GWl7',
		'381',
		'6i4dB6',
		'181',
		'524',
		'HxqYV1',
		'j9PaO9',
		'mm3UXx',
		'8FD8nI',
	];

	call(): void {
		this.configure();
	}

	public configure(): void {
		if (isExperimentEnabled(this.instantConfig, 'peer39', this.pathfinderActiveExperimentVariant)) {
			utils.logger(logGroup, 'Peer39 experiment enabled');

			switch (this.pathfinderActiveExperimentVariant?.name) {
				case this.pathfinderExperimentVariantNames.control:
					utils.logger(logGroup, 'Control triggered');
					addToTargetingExperiment(this.pathfinderExperimentVariantNames.control);
					context.set('experiments.peer39', false);
					break;
				case this.pathfinderExperimentVariantNames.active:
					utils.logger(logGroup, 'Active triggered');
					addToTargetingExperiment(this.pathfinderExperimentVariantNames.active);
					context.set('experiments.peer39', true);
					this.setupPeer39Ids();
					break;
				default:
					utils.logger(logGroup, 'Nothing triggered. Outside of experiment');
					context.set('experiments.peer39', false);
					break;
			}
		} else {
			utils.logger(logGroup, 'Peer39 experiment disabled');
		}
	}

	// Parse peer39 IDs into safety and mapped IDs and set them in the context
	private setupPeer39Ids(): void {
		const peer39Ids = [...(window?.fandomPageTagging?.iab_categories ?? [])];

		// Split out safety and mapped IDs
		const { safetyIds, mappedIds } = this.splitOutSafetyAndMappedIds(peer39Ids);

		// Set peer39 safety IDs
		context.set('peer39.safetyIds', safetyIds);
		if (safetyIds.length > 0) {
			utils.logger(logGroup, `Safety IDs found: ${safetyIds}`);
		} else {
			utils.logger(logGroup, 'No safety IDs found');
		}

		// Set mapped peer39 IDs
		context.set('peer39.mappedIds', mappedIds);
		if (mappedIds.length > 0) {
			utils.logger(logGroup, `Mapped Ids: ${mappedIds}`);
		} else {
			utils.logger(logGroup, 'No mapped IDs found');
		}
	}

	// Split out safety and mapped IDs
	private splitOutSafetyAndMappedIds(ids: string[]): { safetyIds: string[]; mappedIds: string[] } {
		const safetyIds: string[] = [];
		const mappedIds: string[] = [];

		ids.forEach((id) => {
			if (this.SAFETY_IDS.includes(id)) {
				safetyIds.push(id);
			} else {
				mappedIds.push(id);
			}
		});

		// Ensure no duplicates
		return {
			safetyIds: [...new Set(safetyIds)],
			mappedIds: [...new Set(mappedIds)],
		};
	}
}
